import { useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import { fr } from "yup-locales";
import { setLocale } from "yup";
import {
  Menu,
  MenuFormData,
  SectionFormData,
} from "@/redux/services/menu/menu.type";
import {
  cleanerError,
  convertPriceFromCent,
  convertPriceToCent,
  getMenuItemOptionValue,
  isValidUploadedImage,
} from "@/lib/utils";
import {
  useCreateMenuMutation,
  useGetMenuItemOptionsQuery,
  useUpdateMenuMutation,
} from "@/redux/services/menu/menu.api";
import { useAppSelector } from "@/redux/features/hooks";
import { useNavigate } from "react-router-dom";
import { menuItemSchema } from "./useCrudMenuItem";
import { toastApiError } from "@/lib/apiErrors";
setLocale(fr);

// Default values for a menu section
export const sectionDefaultValue: SectionFormData = {
  title: "",
  description: "",
  sortOrder: 1,
  menuItems: [],
};

// Validation schema for a menu section
export const sectionSchema = yup.object().shape({
  title: yup.string().required().label("Titre section"),
  description: yup.string(),
  sortOrder: yup
    .number()
    .required()
    .label("Position")
    .typeError("Position est un champ requis"),
  menuItems: yup.array().of(menuItemSchema),
});

// Validation schema for the entire menu form
const menuFormSchema = yup.object().shape({
  title: yup.string().required().label("Titre"),
  description: yup.string().label("Description"),
  menuSections: yup.array().of(sectionSchema),
});

// Custom hook for creating or updating a menu
export const useCreateMenu = (isEdit?: boolean, menu?: Menu) => {
  const restaurant = useAppSelector((state) => state.user.currentRestaurant);
  const [create, { isLoading }] = useCreateMenuMutation();
  const [update, { isLoading: isLoad }] = useUpdateMenuMutation();
  const { data: menuItemOptions } = useGetMenuItemOptionsQuery({});
  const navigate = useNavigate();

  // Initialize the form with react-hook-form
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    clearErrors,
    reset,
    setValue,
    watch,
    getValues,
    setError,
  } = useForm<MenuFormData>({
    // @ts-ignore
    resolver: yupResolver(menuFormSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "menuSections",
  });

  useEffect(() => {
    if (menuItemOptions) {
      localStorage.setItem(
        "menuItemOptions",
        JSON.stringify(menuItemOptions?.results)
      );
    }
  }, [menuItemOptions]);

  // Clear errors on change
  useEffect(() => {
    if (errors) {
      cleanerError(errors, clearErrors);
    }
  }, [errors, clearErrors]);

  // Save form data to local storage on change if not editing an existing menu
  useEffect(() => {
    if (!menu && !isEdit) {
      const saveDataToLocalStorage = () => {
        const formData = {
          ...getValues(),
        };
        const filteredData = {
          ...formData,
          menuSections: formData?.menuSections?.map((section) => ({
            ...section,
            menuItems: section?.menuItems?.map((menu) => ({
              ...menu,
            })),
          })),
        };
        localStorage.setItem("menuFormData", JSON.stringify(filteredData));
      };
      const subscription = watch(() => {
        saveDataToLocalStorage();
      });

      return () => subscription.unsubscribe();
    }
  }, [watch, getValues, menu]);

  // Load saved form data or existing menu data on component mount
  useEffect(() => {
    const savedData = localStorage.getItem("menuFormData");
    if (menu) {
      reset({
        title: menu.title,
        description: menu.description,
        status: menu.status,
        // @ts-ignore
        menuSections: menu.menuSections.map((section) => ({
          ...section,
          menuItems: section.menuItems.map((menuItem) => ({
            ...menuItem,
            newImageUrl: menuItem.image?.url,
            basePrice: convertPriceFromCent(menuItem.basePrice),
            itemOptions: menuItem.itemOptions.map((option) => ({
              id: option.menuItemOption.id,
              title: option.menuItemOption.title,
              sortOrder: option.menuItemOption.title,
              type: option.menuItemOption.type,
              menuItemOptionValues: option.menuItemOption.menuItemOptionValues,
            })),
          })),
        })),
      });
    } else if (savedData && !isEdit) {
      const parsedData = JSON.parse(savedData);
      reset(parsedData);
    }
  }, [reset, menu]);

  // Submit handler for the form
  const onSubmit = async (data: MenuFormData) => {
    const newMenu = {
      ...data,
      menuSections: data?.menuSections?.map((section) => {
        return {
          ...section,
          sortOrder: Number(section.sortOrder),
          image: isValidUploadedImage(section.image)
            ? section.image
            : undefined,
          menuItems: section?.menuItems?.map((menuItem) => ({
            ...menuItem,
            basePrice: convertPriceToCent(menuItem.basePrice),
            sortOrder: Number(menuItem?.sortOrder),
            image: isValidUploadedImage(menuItem.image)
              ? menuItem.image
              : undefined,
            itemOptions: menu
              ? menuItem.itemOptions && menuItem?.itemOptions?.length > 0
                ? menuItem?.itemOptions?.map((itemOption: any) => {
                    return {
                      menuItemOption: `/api/menu_item_options/${getMenuItemOptionValue(
                        itemOption
                      )}`,
                    };
                  })
                : []
              : menuItem.itemOptions,
          })),
        };
      }),
      restaurant: `/api/restaurants/${restaurant?.id}`,
    };

    if (!menu) {
      // Create new menu
      const res = await create(newMenu);

      if ("data" in res) {
        toast.success(`Menu ajouté avec succès !`);
        navigate("/menus");
        reset();
        localStorage.removeItem("menuFormData");
      } else if ("error" in res) {
        toastApiError(res.error, "Une erreur inconnue est survenue");
      }
    } else {
      // Update existing menu
      const res = await update({
        id: menu?.id,
        data: JSON.stringify({
          ...newMenu,
          "@id": `/api/menus/${menu?.id}`,
        }),
      });

      if ("data" in res) {
        toast.success(`Menu mis à jour avec succès !`);
      } else if ("error" in res) {
        toastApiError(res.error, "Une erreur inconnue est survenue");
      }
    }
  };

  return {
    register,
    errors,
    onSubmit: handleSubmit(onSubmit),
    fields,
    setValue,
    append: () => append(sectionDefaultValue),
    isLoading: menu?.id ? isLoad : isLoading,
    control,
    menuSectionsLength: fields.length,
    menuItemOptions: menuItemOptions?.results?.map((menu) => {
      return {
        label: menu?.title,
        value: menu?.id,
      };
    }),
    getValues,
    setError,
    removeSection: remove,
    appendSection: append,
    watch,
  };
};
